import React, { useEffect, useRef, useState } from 'react';
import { Box, Center, Spinner, Text, Tooltip } from '@chakra-ui/react';
import SaktiFaceRegistryApiService from '../services/saktiFaceRegistryApiService';
import { SaktiBBoxData } from '../types';

interface Props {
  relPath: string;
  pose: string;
  bbox?: SaktiBBoxData;
  size?: number;
}

const SaktiFaceRegistryImageViewer: React.FC<Props> = ({ relPath, pose, bbox, size = 160 }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(false);
      try {
        objectUrl = await SaktiFaceRegistryApiService.getImageBlobUrl(relPath);
        if (!cancelled) setBlobUrl(objectUrl);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [relPath]);

  // Draw image + optional bounding box onto canvas once the blob URL is ready.
  useEffect(() => {
    if (!blobUrl || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = new Image();
    img.onload = () => {
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      ctx.drawImage(img, 0, 0);

      if (bbox) {
        const lw = Math.max(2, img.naturalWidth / 200);
        ctx.strokeStyle = '#00e676';
        ctx.lineWidth = lw;
        ctx.strokeRect(bbox.x, bbox.y, bbox.width, bbox.height);

        const labelH = Math.max(18, img.naturalHeight / 20);
        ctx.fillStyle = 'rgba(0, 230, 118, 0.85)';
        ctx.fillRect(bbox.x, bbox.y - labelH, bbox.width, labelH);
        ctx.fillStyle = '#000';
        ctx.font = `bold ${Math.round(labelH * 0.7)}px sans-serif`;
        ctx.fillText('face', bbox.x + 4, bbox.y - 4);
      }
    };
    img.src = blobUrl;
  }, [blobUrl, bbox]);

  const poseLabel = pose === 'unknown' ? '' : pose.replace(/_/g, ' ');

  return (
    <Tooltip label={poseLabel || relPath.split('/').pop()} placement="top" hasArrow>
      <Box
        borderRadius="md"
        overflow="hidden"
        border="1px solid"
        borderColor={bbox ? 'green.300' : 'gray.200'}
        bg="gray.50"
        w={`${size}px`}
        h={`${size}px`}
        position="relative"
        flexShrink={0}
        cursor="pointer"
      >
        {loading && (
          <Center h="full">
            <Spinner size="sm" color="customGreen.500" />
          </Center>
        )}
        {error && !loading && (
          <Center h="full">
            <Text fontSize="xs" color="gray.400" textAlign="center" px={2}>
              Gagal muat
            </Text>
          </Center>
        )}
        {blobUrl && !error && (
          <canvas
            ref={canvasRef}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        )}
        {poseLabel && (
          <Box
            position="absolute"
            bottom={0}
            left={0}
            right={0}
            bg="blackAlpha.600"
            px={1}
            py="2px"
          >
            <Text fontSize="9px" color="white" noOfLines={1} textAlign="center">
              {poseLabel}
            </Text>
          </Box>
        )}
      </Box>
    </Tooltip>
  );
};

export default SaktiFaceRegistryImageViewer;
